import * as React from "react";

import { CaretDown } from "../icons";
import { cn } from "../../lib/utils";

// Form fields for landing and admin (F123): 44px tall (a comfortable tap
// target), 12px corners, a white field with a hairline border; focus turns
// the border gold with a soft gold halo, an error turns it red. Numbers,
// codes, cards and amounts are `numeric`: left-to-right, tabular digits, and
// Persian or Arabic digits typed in arrive as Latin ones.

const FA = "۰۱۲۳۴۵۶۷۸۹";
const AR = "٠١٢٣٤٥٦٧٨٩";
export const toLatinDigits = (s: string) => s.replace(/[۰-۹٠-٩]/g, (d) => String(FA.includes(d) ? FA.indexOf(d) : AR.indexOf(d)));

/** The field's frame: border, radius, focus and error states. Shared by every field and the unit wrapper. */
export const fieldFrame =
  "rounded-field border border-border bg-background transition-[border-color,box-shadow] aria-[invalid=true]:border-ink-red disabled:cursor-not-allowed disabled:bg-surface disabled:text-muted-foreground";
const focusRing = "outline-none focus-visible:border-brand-strong focus-visible:ring-4 focus-visible:ring-brand/30 focus-visible:outline-none";

/**
 * A whole one-line field's classes, for inputs that can't be <Input> (e.g. landing's DigitInput).
 * 44px, or 36px where an area sets data-density="compact" (F191).
 */
export const fieldClass = cn("placeholder:text-muted-foreground h-(--size-field) w-full min-w-0 px-3.5 text-sm", fieldFrame, focusRing);

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** LTR, tabular digits, Persian/Arabic digits converted to Latin as typed. */
  numeric?: boolean;
  /** Shown after the value (in the field), e.g. «تومان», «٪», «گرم». */
  unit?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(({ className, numeric, unit, onChange, ...props }, ref) => {
  const handleChange: React.ChangeEventHandler<HTMLInputElement> | undefined =
    numeric && onChange
      ? (event) => {
          const latin = toLatinDigits(event.target.value);
          if (latin !== event.target.value) event.target.value = latin;
          onChange(event);
        }
      : onChange;
  const numericProps = numeric ? { dir: "ltr" as const, inputMode: props.inputMode ?? ("decimal" as const) } : {};

  const input = (
    <input
      ref={ref}
      {...numericProps}
      {...props}
      onChange={handleChange}
      className={cn(
        "placeholder:text-muted-foreground h-(--size-field) w-full min-w-0 px-3.5 text-sm",
        numeric && "text-left tabular-nums",
        unit ? "bg-transparent outline-none" : [fieldFrame, focusRing],
        !unit && className,
      )}
    />
  );
  if (!unit) return input;
  // With a unit, the frame moves to a wrapper so the unit sits inside it.
  return (
    <span
      className={cn(
        fieldFrame,
        "focus-within:border-brand-strong focus-within:ring-brand/30 flex items-center focus-within:ring-4",
        props["aria-invalid"] && "border-ink-red",
        props.disabled && "bg-surface",
        className,
      )}
    >
      {input}
      <span className="text-muted-foreground shrink-0 pe-3.5 text-xs">{unit}</span>
    </span>
  );
});
Input.displayName = "Input";

/** A 36px field for filter bars and table rows (admin). */
export const compactField = cn("placeholder:text-muted-foreground h-9 min-w-0 px-2.5 text-sm", fieldFrame, focusRing);
/** A multi-line field's classes. */
export const textareaClass = cn("placeholder:text-muted-foreground min-h-24 w-full px-3.5 py-3 text-sm leading-6", fieldFrame, focusRing);
/** A focus ring for a wrapper that holds a bare input (search box with an icon). */
export const fieldWithin = cn(fieldFrame, "focus-within:border-brand-strong focus-within:ring-brand/30 focus-within:ring-4");

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => (
  <textarea ref={ref} {...props} className={cn(textareaClass, className)} />
));
Textarea.displayName = "Textarea";

/** Native select (the phone's own picker), dressed as a field with a caret. */
export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({ className, children, ...props }, ref) => (
  <span className={cn("relative block", className)}>
    <select ref={ref} {...props} className={cn("h-(--size-field) w-full appearance-none ps-3.5 pe-9 text-sm", fieldFrame, focusRing)}>
      {children}
    </select>
    <CaretDown size={14} className="text-muted-foreground pointer-events-none absolute end-3.5 top-1/2 -translate-y-1/2" />
  </span>
));
Select.displayName = "Select";

/**
 * A labelled field: label above, then the control, then the error (red) or
 * the hint. The label wraps the control, so clicking it focuses the field.
 */
export function Field({
  label,
  hint,
  error,
  className,
  children,
}: {
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-sm font-medium">{label}</span>
      {children}
      {error ? (
        <span role="alert" className="text-ink-red text-xs leading-5">
          {error}
        </span>
      ) : hint ? (
        <span className="text-muted-foreground text-xs leading-5">{hint}</span>
      ) : null}
    </label>
  );
}
